import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid } from '@material-ui/core';
import { MutationFunction } from 'react-apollo';

import {
  ManualNotificationsItem,
  ManualNotificationsItemsList,
  GetManualNotificationsQueryVariables,
  SegmentStatus,
  UserSegment,
  GetManualNotificationsDocument,
} from 'api';
import { EmptyState, LoadMoreButton, Queries } from 'components';
import { deleteMessage } from 'apollo/reducers';

import Create from '../components/Messages/Create';
import Edit from '../components/Messages/Edit';
import Scheduled from '../components/Messages/Scheduled';
import Sent from '../components/Messages/Sent';
import Delete from '../components/Messages/Delete';

interface IQueryResults {
  scheduledData: ManualNotificationsItemsList;
  scheduledOptions: Queries.IGetManualNotificationsOptions;
  sentData: ManualNotificationsItemsList;
  sentOptions: Queries.IGetManualNotificationsOptions;
  segments: UserSegment[];
}

const SCHEDULED_VARIABLES: GetManualNotificationsQueryVariables = {
  scheduled: true,
  data: { limit: 10 },
};
const SENT_VARIABLES: GetManualNotificationsQueryVariables = {
  scheduled: false,
  data: { limit: 10 },
};

const Messages: FC = () => {
  const [editing, setEditing] = useState(false);
  const [checkedItem, setCheckedItem] = useState(null);
  const { t } = useTranslation();

  function handleDelete(mutation: MutationFunction) {
    return mutation({
      variables: {
        id: checkedItem.id,
      },
      update: (cache, result) => {
        deleteMessage({
          cache,
          result,
          variables: { scheduled: true },
          query: GetManualNotificationsDocument,
        });
        setCheckedItem(null);
      },
      optimisticResponse: {
        deleteManualNotification: checkedItem,
      },
    });
  }

  function handleChecked(event: ChangeEvent<HTMLInputElement>, item: ManualNotificationsItem) {
    const { checked } = event.target;
    setCheckedItem(checked ? item : null);
  }

  function renderActions() {
    if (!checkedItem) {
      return null;
    }

    return (
      <Fragment>
        <Delete onDelete={handleDelete} />
        <Button variant="outlined" size="small" onClick={() => setEditing(true)}>
          {t('common:edit')}
        </Button>
      </Fragment>
    );
  }

  function renderPaginator(options: Queries.IGetManualNotificationsOptions) {
    const { exhausted, loading, onLoadMore } = options;
    return !exhausted && <LoadMoreButton loading={loading} onClick={onLoadMore} />;
  }

  function renderRoot(queryResults: IQueryResults) {
    const { scheduledData, scheduledOptions, sentData, sentOptions, segments } = queryResults;

    if (editing) {
      return (
        <Edit
          queryVariables={SCHEDULED_VARIABLES}
          segments={segments}
          item={checkedItem}
          onEdit={() => setEditing(false)}
        />
      );
    }

    if (!scheduledData.items && !sentData.items) {
      return (
        <Grid item>
          <EmptyState name="messages" />
        </Grid>
      );
    }

    return (
      <Grid container direction="column" spacing={5}>
        <Grid item>
          <Create segments={segments} queryVariables={SCHEDULED_VARIABLES} />
        </Grid>
        <Grid item>
          <Scheduled
            notificationsList={scheduledData}
            onChecked={handleChecked}
            checkedItem={checkedItem}
            actions={renderActions()}
            paginator={renderPaginator(scheduledOptions)}
          />
        </Grid>
        <Grid item>
          <Sent notificationsList={sentData} paginator={renderPaginator(sentOptions)} />
        </Grid>
      </Grid>
    );
  }

  return (
    <Queries.GetManualNotifications variables={SCHEDULED_VARIABLES}>
      {(scheduledData, scheduledOptions) => (
        <Queries.GetManualNotifications variables={SENT_VARIABLES}>
          {(sentData, sentOptions) => (
            <Queries.GetUserSegments status={SegmentStatus.Active}>
              {segments =>
                renderRoot({ scheduledData, scheduledOptions, sentData, sentOptions, segments })
              }
            </Queries.GetUserSegments>
          )}
        </Queries.GetManualNotifications>
      )}
    </Queries.GetManualNotifications>
  );
};

export default Messages;
